import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  ArrowDownIcon,
  ArrowUpIcon,
  Bars3Icon,
  CheckCircleIcon,
  LockClosedIcon,
} from "@heroicons/react/24/outline";
import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import { useNavigate } from "react-router-dom";

import {
  QUEUE_REPORT_DRAFT,
  SAVE_REPORT_DRAFT,
} from "../../../graphql/mutation/ReportJob";
import { FETCH_BROCHURE_WIZARD_INSTANCES } from "../../../graphql/query/Instance";
import { FETCH_LIST_ITEMS } from "../../../graphql/query/ListItems";
import {
  REPORT_SECTIONS,
  reportSectionMeta,
} from "../../../modules/Reporting/utils";
import { Button } from "../../ui";
import ThumbnailCarousel from "../../ui/ThumbnailCarousel";
import BrochurePageList from "./BrochurePageList";
import {
  buildDraftInput,
  buildWizardInstancesInput,
  coverSummaryText,
  filterInstanceSummaries,
  initialIncludePrices,
  initialInstanceOrder,
  instanceIdsFromListItems,
  instanceSummariesById,
  isInstanceConfigured,
  removeInstanceFromOrder,
  reorderList,
  sectionImagesSummary,
  sectionPagesOf,
  sectionSummary,
  selectWizardEdges,
  suggestedCoverText,
  wizardStateFromDraftPayload,
} from "./wizardUtils";

const AUTOSAVE_DEBOUNCE_MS = 1000;

// A select list's items, without the entity each one points at — the wizard
// only needs the instance ids out of their `meta`, then loads those instances
// the same way as any other cohort. Built once so Apollo parses it once.
const FETCH_BROCHURE_LIST_ITEMS = FETCH_LIST_ITEMS();

// Brochures are tracked in the PDF Generator section, not Reporting.
const PDF_GENERATOR_PATH = reportSectionMeta(
  REPORT_SECTIONS.PDF_GENERATOR,
).path;

const STAGES = {
  ARRANGE: "arrange",
  COVER: "cover",
  CONFIGURE: "configure",
  REVIEW: "review",
};

const EMPTY_REPORT_COVER = {
  instanceId: null,
  archiveId: null,
  title: "",
  subtitle: "",
  dates: "",
  location: "",
};

const SAVE_STATE_LABELS = {
  saving: "Saving draft…",
  saved: "Draft saved",
  error: "Draft could not be saved",
};

/** One draggable row in the arrange step. */
const SortableInstanceRow = ({
  summary,
  index,
  total,
  onMoveUp,
  onMoveDown,
  onConfigure,
  onRemove,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: summary.id });

  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
    zIndex: isDragging ? 10 : undefined,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex flex-wrap items-center gap-3 rounded-lg border border-gray-200 bg-white px-3 py-2 lg:flex-nowrap"
    >
      <button
        type="button"
        className="cursor-grab text-gray-400 hover:text-gray-700"
        title="Drag to reorder"
        {...attributes}
        {...listeners}
      >
        <Bars3Icon className="h-5 w-5" />
      </button>
      <div className="w-6 text-sm font-bold text-gray-500">{index + 1}</div>
      {summary.thumbnail ? (
        <img
          src={summary.thumbnail}
          alt={`Instance ${summary.name}`}
          className="h-12 w-12 shrink-0 object-cover"
        />
      ) : (
        <div className="h-12 w-12 shrink-0 bg-gray-100" />
      )}
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-medium">{summary.name}</div>
        <div className="truncate text-xs text-gray-500">
          {[summary.artistName, summary.title].filter(Boolean).join(" — ")}
        </div>
      </div>
      <div className="order-last flex w-full justify-end gap-4 lg:order-none lg:w-auto">
        <button
          type="button"
          className="text-xs font-medium text-blue-600 hover:text-blue-800"
          onClick={onConfigure}
        >
          Configure
        </button>
        <button
          type="button"
          disabled={total === 1}
          className="text-xs font-medium text-red-600 enabled:hover:text-red-800 disabled:cursor-not-allowed disabled:opacity-30"
          title={
            total === 1
              ? "A report must contain at least one instance"
              : "Remove from report"
          }
          onClick={onRemove}
        >
          Remove
        </button>
      </div>
      <div className="flex flex-col">
        <button
          type="button"
          disabled={index === 0}
          className="text-gray-400 enabled:hover:text-gray-700 disabled:opacity-30"
          title="Move up"
          onClick={onMoveUp}
        >
          <ArrowUpIcon className="h-4 w-4" />
        </button>
        <button
          type="button"
          disabled={index === total - 1}
          className="text-gray-400 enabled:hover:text-gray-700 disabled:opacity-30"
          title="Move down"
          onClick={onMoveDown}
        >
          <ArrowDownIcon className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

/** Compact instance list shown beside the configure / review steps. */
const InstanceNavigationList = ({
  orderedIds,
  summariesById,
  configurationsById,
  currentId,
  onSelect,
}) => (
  <div className="flex max-h-40 w-full shrink-0 gap-1 overflow-x-auto overflow-y-auto pb-2 lg:max-h-[60vh] lg:w-56 lg:flex-col lg:pr-2">
    {orderedIds.map((id, index) => {
      const summary = summariesById.get(id);
      const configured = isInstanceConfigured(configurationsById[id]);
      return (
        <button
          key={id}
          type="button"
          onClick={() => onSelect(id)}
          className={`flex min-w-40 items-center gap-2 rounded px-2 py-1.5 text-left text-xs lg:min-w-0 ${
            id === currentId
              ? "bg-blue-50 font-semibold text-blue-800"
              : "text-gray-700 hover:bg-gray-50"
          }`}
        >
          <span className="w-5 shrink-0 text-gray-400">{index + 1}</span>
          <span className="min-w-0 flex-1 truncate">
            {summary?.name || `Instance ${id}`}
          </span>
          {configured ? (
            <CheckCircleIcon className="h-4 w-4 shrink-0 text-green-600" />
          ) : null}
        </button>
      );
    })}
  </div>
);

/**
 * The whole-report front cover editor: exhibition / series text plus an
 * image chosen by first filtering the cohort's instances (reference +
 * primary image) and then picking one of that instance's images.
 */
// A one-work brochure covers itself, so the fields are labelled as the work's
// own details; a cohort's cover is about the show rather than a work.
const COVER_FIELD_LABELS = {
  work: [
    { key: "title", label: "Artist", placeholder: "e.g. Tracey Emin" },
    { key: "subtitle", label: "Work Title", placeholder: "e.g. Untitled" },
    { key: "dates", label: "Dates", placeholder: "e.g. 2024" },
    {
      key: "location",
      label: "Location",
      placeholder: "e.g. White Cube Paris",
    },
  ],
  cohort: [
    {
      key: "title",
      label: "Title (exhibition / series)",
      placeholder: "e.g. the exhibition or series name",
    },
    { key: "subtitle", label: "Subtitle", placeholder: "e.g. the artist" },
    { key: "dates", label: "Dates", placeholder: "e.g. 2019—2024" },
    {
      key: "location",
      label: "Location",
      placeholder: "e.g. White Cube Paris",
    },
  ],
};

const ReportCoverEditor = ({
  orderedIds,
  summariesById,
  cover,
  onChange,
  disabled,
}) => {
  const [filter, setFilter] = useState("");

  const summaries = orderedIds.map(id => summariesById.get(id)).filter(Boolean);
  const filtered = filterInstanceSummaries(summaries, filter);
  const chosen = cover.instanceId ? summariesById.get(cover.instanceId) : null;
  const primaryArchiveId = chosen?.images?.[0]?.archiveId ?? null;
  const selectedArchiveId = cover.archiveId ?? primaryArchiveId;

  return (
    <div className="flex flex-col gap-4">
      <div className="grid max-w-5xl grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
        {COVER_FIELD_LABELS[orderedIds.length === 1 ? "work" : "cohort"].map(
          field => (
            <label key={field.key} className="flex flex-col gap-1 text-sm">
              <span className="font-medium">{field.label}</span>
              <input
                type="text"
                className="rounded border border-gray-300 px-2 py-1.5 text-sm"
                value={cover[field.key]}
                disabled={disabled}
                placeholder={field.placeholder}
                onChange={event =>
                  onChange({ [field.key]: event.target.value })
                }
              />
            </label>
          ),
        )}
      </div>

      <div>
        <div className="text-sm font-medium">Cover image</div>
        <div className="mt-1 text-xs text-gray-500">
          Pick the instance the cover image comes from, then choose one of its
          images. Left unset, the instance&apos;s primary image is used.
        </div>
        <input
          type="text"
          className="mt-2 w-full rounded border border-gray-300 px-2 py-1.5 text-sm sm:max-w-72"
          value={filter}
          disabled={disabled}
          placeholder="Filter by reference, artist or title…"
          onChange={event => setFilter(event.target.value)}
        />
        <div className="mt-3 grid max-h-[30vh] grid-cols-2 gap-3 overflow-y-auto pr-1 sm:grid-cols-4 lg:grid-cols-6">
          {filtered.map(summary => {
            const selected = summary.id === cover.instanceId;
            return (
              <button
                key={summary.id}
                type="button"
                disabled={disabled}
                onClick={() =>
                  onChange({ instanceId: summary.id, archiveId: null })
                }
                className={`flex flex-col items-center gap-1 rounded border p-2 text-xs transition ${
                  selected
                    ? "border-blue-600 ring-2 ring-blue-400"
                    : "border-gray-200 hover:border-gray-400"
                }`}
                title={[summary.artistName, summary.title]
                  .filter(Boolean)
                  .join(" — ")}
              >
                {summary.thumbnail ? (
                  <img
                    src={summary.thumbnail}
                    alt={`Instance ${summary.name}`}
                    className="h-16 w-16 object-cover"
                  />
                ) : (
                  <div className="h-16 w-16 bg-gray-100" />
                )}
                <span className="w-full truncate text-center font-medium">
                  {summary.name}
                </span>
              </button>
            );
          })}
          {filtered.length === 0 ? (
            <div className="col-span-full text-xs text-gray-500">
              No instances match that filter.
            </div>
          ) : null}
        </div>
      </div>

      {chosen ? (
        <div>
          <div className="text-xs font-medium text-gray-700">
            Images from {chosen.name}
          </div>
          <ThumbnailCarousel
            className="mt-1"
            label="cover images"
            selectedIndex={(chosen.images || []).findIndex(
              image => image.archiveId === selectedArchiveId,
            )}
          >
            {(chosen.images || []).map(image => {
              const selected = selectedArchiveId === image.archiveId;
              return (
                <button
                  key={image.archiveId}
                  type="button"
                  disabled={disabled}
                  onClick={() => onChange({ archiveId: image.archiveId })}
                  className={`relative h-24 w-24 overflow-hidden border-2 transition ${
                    selected
                      ? "border-blue-600 opacity-100 ring-2 ring-blue-400 ring-offset-2"
                      : "border-transparent opacity-60 hover:opacity-100"
                  }`}
                  title={`Archive ${image.archiveId}`}
                >
                  <img
                    src={image.imgT}
                    alt={`Artwork image ${image.archiveId}`}
                    className="h-full w-full object-cover"
                  />
                </button>
              );
            })}
          </ThumbnailCarousel>
          {(chosen.images || []).length === 0 ? (
            <div className="mt-1 text-xs text-gray-500">
              This instance&apos;s artwork has no images — the cover will be
              text-only.
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

/**
 * The document's fixed first page in the Arrange list: it can't be moved or
 * dropped, only edited.
 */
const CoverRow = ({ cover, summary, included, onEdit }) => (
  <div className="flex flex-wrap items-center gap-3 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 lg:flex-nowrap">
    <LockClosedIcon className="h-5 w-5 shrink-0 text-gray-300" />
    <div className="w-6" />
    {summary?.thumbnail ? (
      <img
        src={summary.thumbnail}
        alt={`Instance ${summary.name}`}
        className="h-12 w-12 shrink-0 object-cover"
      />
    ) : (
      <div className="h-12 w-12 shrink-0 bg-gray-100" />
    )}
    <div className="min-w-0 flex-1">
      <div className="truncate text-sm font-medium">
        Front cover{included ? "" : " (off)"}
      </div>
      <div className="truncate text-xs text-gray-500">
        {included ? coverSummaryText(cover) : "Not included in the PDF"}
      </div>
    </div>
    <button
      type="button"
      className="text-xs font-medium text-blue-600 hover:text-blue-800"
      onClick={onEdit}
    >
      Edit
    </button>
  </div>
);

/**
 * Multi-instance Brochure Wizard: arrange the instances (starting in the
 * order of the search), pick images and pages per instance, and queue the
 * brochure — with the configuration autosaved to a draft ReportJob along the
 * way so it can be resumed from the PDF Generator section.
 *
 * The cohort can arrive four ways: as instance ids / a search from the
 * Instances module, as a saved select list, as `artworkIds` from Artworks (each
 * artwork collapses to its most recent instance) or as `exhibitionIds` from
 * Exhibitions (every work hung in them). Whichever way, the draft it saves is a
 * plain ordered list of instances.
 */
const BrochureWizard = ({
  closeModal,
  ids = [],
  artworkIds = [],
  exhibitionIds = [],
  listId,
  search,
  select,
  includePrices = true,
  draftJob = null,
}) => {
  const navigate = useNavigate();

  const draftState = useMemo(
    () => wizardStateFromDraftPayload(draftJob?.payload),
    [draftJob],
  );

  // A resumed draft already holds concrete instance ids, so it never needs the
  // artwork / exhibition / select list expansion again.
  const isResumedDraft = draftState.orderedIds.length > 0;

  // A select list is the cohort only when the user asked for the whole list
  // rather than the rows they ticked, matching `buildExportPayload`. An
  // artwork / exhibition cohort defines the selection on its own, and the list
  // open behind it holds those entities rather than instances, so it is
  // ignored there.
  const savedListId =
    !isResumedDraft &&
    select === "all" &&
    artworkIds.length === 0 &&
    exhibitionIds.length === 0
      ? listId ?? null
      : null;

  const { data: listData, loading: listLoading } = useQuery(
    FETCH_BROCHURE_LIST_ITEMS,
    {
      variables: { input: { listId: savedListId } },
      skip: savedListId == null,
      fetchPolicy: "cache-and-network",
    },
  );
  const listItemIds = useMemo(
    () => instanceIdsFromListItems(listData?.listItems?.edges || []),
    [listData],
  );

  // A select list resolves in two steps — its items, then those instances — so
  // there is a moment where it has no ids yet. Nothing is loaded until it does,
  // or the search still on screen would stand in for the list the user asked
  // for.
  const listUnresolved = savedListId != null && listItemIds.length === 0;

  const queryInput = useMemo(() => {
    if (isResumedDraft) {
      return buildWizardInstancesInput({ ids: draftState.orderedIds });
    }
    if (listUnresolved) return null;
    return buildWizardInstancesInput({
      ids,
      artworkIds,
      exhibitionIds,
      listItemIds,
      search,
      select,
    });
  }, [
    isResumedDraft,
    draftState,
    listUnresolved,
    ids,
    artworkIds,
    exhibitionIds,
    listItemIds,
    search,
    select,
  ]);

  // Whether the brochure prints sale prices, for the whole document — the user's
  // to change on the review step. A draft remembers what it was saved with; a
  // fresh wizard starts where its entry point put it, and Artworks starts it off
  // because the works there each have several instances to take a price from.
  const [pricesIncluded, setPricesIncluded] = useState(
    initialIncludePrices({ draftPayload: draftJob?.payload, includePrices }),
  );

  // Likewise which module the cohort was picked in, which the list shows as the
  // report's type.
  const cohortSource = draftJob
    ? {
        artworkIds: draftState.artworkIds,
        exhibitionIds: draftState.exhibitionIds,
      }
    : { artworkIds, exhibitionIds };

  const { data, loading, error } = useQuery(FETCH_BROCHURE_WIZARD_INSTANCES, {
    variables: { input: queryInput },
    skip: queryInput == null,
    fetchPolicy: "cache-and-network",
  });

  const edges = useMemo(
    () =>
      selectWizardEdges({
        edges: data?.instancesV2?.edges || [],
        artworkIds: isResumedDraft ? [] : artworkIds,
      }),
    [data, artworkIds, isResumedDraft],
  );
  const summariesById = useMemo(() => instanceSummariesById(edges), [edges]);

  const [stage, setStage] = useState(STAGES.ARRANGE);
  const [orderedIds, setOrderedIds] = useState([]);
  const [configurationsById, setConfigurationsById] = useState(
    draftState.configurationsById,
  );
  const [currentId, setCurrentId] = useState(null);

  // Whole-report front cover. Existing drafts omit the switch and therefore
  // keep the default-on behaviour.
  const [reportCoverIncluded, setReportCoverIncluded] = useState(
    draftState.includeReportCover,
  );
  const [reportCover, setReportCover] = useState(
    draftState.reportCover || EMPTY_REPORT_COVER,
  );

  // The biography pages, keyed by artist rather than by work: an artist in three
  // of the cohort's works reads the same on all three of their pages, and their
  // portrait is uploaded once. Untouched artists aren't in here at all, and
  // print what their contact record says, the portrait placeholder, and the
  // default caption naming them.
  const [biographies, setBiographies] = useState(draftState.biographies);
  const [artistPortraits, setArtistPortraits] = useState(
    draftState.artistPortraits,
  );
  const [portraitCaptions, setPortraitCaptions] = useState(
    draftState.portraitCaptions,
  );

  const [draftId, setDraftId] = useState(draftJob?.id ?? null);
  const [saveState, setSaveState] = useState(draftJob ? "saved" : "idle");
  const [queueError, setQueueError] = useState(null);
  const [queuing, setQueuing] = useState(false);
  const [queuedJob, setQueuedJob] = useState(null);

  const [saveReportDraft] = useMutation(SAVE_REPORT_DRAFT);
  const [queueReportDraft] = useMutation(QUEUE_REPORT_DRAFT);

  // Seed the order once the cohort arrives: a resumed draft keeps its saved
  // order and a select list its own, while everything else starts in the order
  // of the search. A single work has nothing to arrange, so it opens on the
  // front cover instead.
  useEffect(() => {
    if (edges.length > 0 && orderedIds.length === 0) {
      const order = initialInstanceOrder({
        edges,
        preferredOrder:
          draftState.orderedIds.length > 0
            ? draftState.orderedIds
            : listItemIds,
      });
      setOrderedIds(order);
      setStage(order.length > 1 ? STAGES.ARRANGE : STAGES.COVER);
    }
  }, [edges, orderedIds.length, draftState, listItemIds]);

  // Auto-fill the front cover once the cohort is known: a single work fills in
  // its own artist, title and dates, while a cohort takes a shared series (or a
  // shared artist). Location remains editable because it is a report-level
  // choice. The image defaults to the first instance's primary image. Anything
  // the user already set is left alone.
  useEffect(() => {
    if (orderedIds.length === 0 || summariesById.size === 0) return;
    setReportCover(previous => {
      if (
        previous.instanceId != null ||
        previous.title ||
        previous.subtitle ||
        previous.dates ||
        previous.location
      ) {
        return previous;
      }
      return {
        ...previous,
        instanceId: orderedIds[0],
        ...suggestedCoverText(
          orderedIds.map(id => summariesById.get(id)).filter(Boolean),
        ),
      };
    });
  }, [orderedIds, summariesById]);

  // ----------------------------------------------------------- autosave
  // Latest wizard state in a ref so the debounced save (and the flush before
  // Generate) never persists a stale snapshot.
  const draftStateRef = useRef({});
  draftStateRef.current = {
    orderedIds,
    configurationsById,
    draftId,
    reportCover,
    includeReportCover: reportCoverIncluded,
    includePrices: pricesIncluded,
    biographies,
    artistPortraits,
    portraitCaptions,
    ...cohortSource,
  };

  // Serialise saves: overlapping calls chain onto the previous save so two
  // in-flight creates can never race each other into two draft rows, and the
  // second save always sees the first's draft id.
  const saveChainRef = useRef(Promise.resolve(null));
  const persistDraft = () => {
    const run = async () => {
      const snapshot = draftStateRef.current;
      if (snapshot.orderedIds.length === 0) return null;
      setSaveState("saving");
      try {
        const { data: saved } = await saveReportDraft({
          variables: {
            input: buildDraftInput({
              id: snapshot.draftId,
              orderedIds: snapshot.orderedIds,
              configurationsById: snapshot.configurationsById,
              reportCover: snapshot.reportCover,
              includeReportCover: snapshot.includeReportCover,
              includePrices: snapshot.includePrices,
              artworkIds: snapshot.artworkIds,
              exhibitionIds: snapshot.exhibitionIds,
              biographies: snapshot.biographies,
              artistPortraits: snapshot.artistPortraits,
              portraitCaptions: snapshot.portraitCaptions,
            }),
          },
        });
        const result = saved?.saveReportDraft;
        if (!result?.success) throw new Error("save failed");
        setDraftId(result.reportJob.id);
        draftStateRef.current.draftId = result.reportJob.id;
        setSaveState("saved");
        return result.reportJob.id;
      } catch {
        setSaveState("error");
        return null;
      }
    };
    saveChainRef.current = saveChainRef.current.then(run, run);
    return saveChainRef.current;
  };
  const persistDraftRef = useRef(persistDraft);
  persistDraftRef.current = persistDraft;

  // Debounced autosave on any change to the order or per-instance
  // configuration. `dirty` flips on the first user change so simply opening
  // and closing the wizard never creates a draft row.
  const dirtyRef = useRef(false);
  const saveTimerRef = useRef(null);
  const scheduleSave = () => {
    dirtyRef.current = true;
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      persistDraftRef.current();
    }, AUTOSAVE_DEBOUNCE_MS);
  };
  useEffect(() => () => clearTimeout(saveTimerRef.current), []);

  // -------------------------------------------------------- state updates
  const applyOrder = nextIds => {
    setOrderedIds(nextIds);
    scheduleSave();
  };

  const updateConfiguration = (instanceId, patch) => {
    setConfigurationsById(previous => ({
      ...previous,
      [instanceId]: {
        imageSlots: {},
        brochurePages: {},
        publicCollections: {},
        pages: [],
        ...previous[instanceId],
        ...patch,
      },
    }));
    scheduleSave();
  };

  const currentConfiguration = configurationsById[currentId] || {
    imageSlots: {},
    brochurePages: {},
    publicCollections: {},
    pages: [],
  };
  const currentSummary = summariesById.get(currentId);

  // A document covering several works starts some pages off — the biography,
  // which would otherwise repeat for every artist in the cohort. Matches the
  // renderer, so an untouched brochure prints what the wizard showed.
  //
  // It also decides how much of the wizard there is to walk: a brochure of one
  // work has no order to arrange and no other work to step to, so it skips
  // straight from its front cover to its pages.
  const multiInstance = orderedIds.length > 1;

  // The section the current work prints: the list the user arranged, or the
  // default one it starts with.
  const currentPages = sectionPagesOf(currentConfiguration, { multiInstance });

  const updatePublicCollections = (artistId, text) =>
    updateConfiguration(currentId, {
      publicCollections: {
        ...currentConfiguration.publicCollections,
        [artistId]: text,
      },
    });

  const updateBiography = (artistId, text) => {
    setBiographies(previous => ({ ...previous, [artistId]: text }));
    scheduleSave();
  };

  // A handle names an uploaded portrait; null takes the artist back to the
  // placeholder.
  const updateArtistPortrait = (artistId, handle) => {
    setArtistPortraits(previous =>
      handle == null
        ? Object.fromEntries(
            Object.entries(previous).filter(
              ([key]) => key !== String(artistId),
            ),
          )
        : { ...previous, [artistId]: handle },
    );
    scheduleSave();
  };

  const updatePortraitCaption = (artistId, text) => {
    setPortraitCaptions(previous => ({ ...previous, [artistId]: text }));
    scheduleSave();
  };

  const updateCover = patch => {
    setReportCover(previous => ({ ...previous, ...patch }));
    scheduleSave();
  };

  const updateReportCoverIncluded = included => {
    setReportCoverIncluded(included);
    scheduleSave();
  };

  const updatePricesIncluded = shown => {
    setPricesIncluded(shown);
    scheduleSave();
  };

  const removeInstance = instanceId => {
    if (orderedIds.length <= 1) return;
    const summary = summariesById.get(instanceId);
    // eslint-disable-next-line no-alert
    if (
      !window.confirm(
        `Remove ${summary?.name || `instance ${instanceId}`} from this report?`,
      )
    ) {
      return;
    }

    const nextIds = removeInstanceFromOrder(orderedIds, instanceId);
    applyOrder(nextIds);
    setConfigurationsById(previous =>
      Object.fromEntries(
        Object.entries(previous).filter(
          ([id]) => Number(id) !== Number(instanceId),
        ),
      ),
    );
    if (reportCover.instanceId === instanceId) {
      setReportCover(previous => ({
        ...previous,
        instanceId: nextIds[0] ?? null,
        archiveId: null,
      }));
    }
    if (currentId === instanceId) setCurrentId(nextIds[0] ?? null);
  };

  // ---------------------------------------------------------- navigation
  const goToInstance = instanceId => {
    setCurrentId(instanceId);
    setStage(STAGES.CONFIGURE);
  };

  const currentIndex = orderedIds.indexOf(currentId);
  const goToNext = () => {
    if (currentIndex < orderedIds.length - 1) {
      setCurrentId(orderedIds[currentIndex + 1]);
    } else {
      setStage(STAGES.REVIEW);
    }
  };
  const goToPrevious = () => {
    if (currentIndex > 0) {
      setCurrentId(orderedIds[currentIndex - 1]);
    } else {
      setStage(STAGES.COVER);
    }
  };

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const from = orderedIds.indexOf(active.id);
    const to = orderedIds.indexOf(over.id);
    applyOrder(reorderList(orderedIds, from, to));
  };

  // ------------------------------------------------------------- generate
  const handleGenerate = async () => {
    setQueuing(true);
    setQueueError(null);
    try {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      // Flush the latest state so the queued payload matches what's on
      // screen, even mid-debounce.
      const savedDraftId = (await persistDraft()) ?? draftId;
      if (savedDraftId == null) {
        throw new Error("The draft could not be saved — please try again.");
      }

      const { data: queued } = await queueReportDraft({
        variables: { id: savedDraftId },
      });
      const result = queued?.queueReportDraft;
      if (!result?.success) {
        const message = Array.isArray(result?.error)
          ? result.error.join(", ")
          : result?.error || "Failed to queue the PDF";
        throw new Error(message);
      }
      setQueuedJob(result.reportJob);
    } catch (err) {
      setQueueError(err.message || "Failed to queue the PDF");
    } finally {
      setQueuing(false);
    }
  };

  // ------------------------------------------------------------ rendering
  if (queuedJob) {
    return (
      <div className="mt-6 flex w-full flex-col">
        <div className="text-2xl font-bold">PDF Generator Queued</div>
        <div className="pt-4 text-base font-normal">
          Your PDF for {orderedIds.length} instance
          {orderedIds.length === 1 ? "" : "s"} is being generated in the
          background. Track its progress and download the finished file from the
          PDF Generator section.
        </div>
        <div className="flex flex-col-reverse gap-3 pt-6 sm:flex-row sm:flex-wrap sm:justify-end sm:gap-4">
          <Button
            action="default"
            label="Close"
            onClick={() => closeModal?.()}
          />
          <Button
            label="View in PDF Generator"
            onClick={() => {
              closeModal?.();
              navigate(PDF_GENERATOR_PATH);
            }}
          />
        </div>
      </div>
    );
  }

  const stageTitle = {
    [STAGES.ARRANGE]: "Arrange Instances",
    [STAGES.COVER]: "Front Cover",
    [STAGES.CONFIGURE]: multiInstance ? "Configure Instances" : "Pages",
    [STAGES.REVIEW]: "Review & Generate",
  }[stage];

  const footer = (
    <div className="flex flex-col gap-3 border-t border-gray-100 pt-4 lg:flex-row lg:items-center lg:justify-between">
      <div className="flex flex-wrap items-center gap-4">
        <Button
          action="default"
          label="Close"
          disabled={queuing}
          onClick={() => closeModal?.()}
        />
        {SAVE_STATE_LABELS[saveState] ? (
          <span
            className={`text-xs ${
              saveState === "error" ? "text-red-600" : "text-gray-400"
            }`}
          >
            {SAVE_STATE_LABELS[saveState]}
          </span>
        ) : null}
      </div>
      <div className="flex flex-wrap items-center justify-end gap-3">
        {queueError ? (
          <span className="text-sm text-red-600">{queueError}</span>
        ) : null}
        {/* The front cover is the first step of a one-work brochure, so there
            is nowhere to go back to from it. */}
        {stage !== STAGES.ARRANGE &&
        !(stage === STAGES.COVER && !multiInstance) ? (
          <Button
            action="default"
            label="Back"
            disabled={queuing}
            onClick={() => {
              if (stage === STAGES.REVIEW) {
                goToInstance(orderedIds[orderedIds.length - 1]);
              } else if (stage === STAGES.COVER) {
                setStage(STAGES.ARRANGE);
              } else {
                goToPrevious();
              }
            }}
          />
        ) : null}
        {stage === STAGES.ARRANGE ? (
          <Button
            label="Next: Front Cover"
            disabled={orderedIds.length === 0}
            onClick={() => setStage(STAGES.COVER)}
          />
        ) : null}
        {stage === STAGES.COVER ? (
          <Button
            label={multiInstance ? "Next: Configure" : "Next: Pages"}
            disabled={orderedIds.length === 0}
            onClick={() => goToInstance(orderedIds[0])}
          />
        ) : null}
        {stage === STAGES.CONFIGURE ? (
          <Button
            label={
              currentIndex < orderedIds.length - 1
                ? "Next Instance"
                : "Next: Review"
            }
            onClick={goToNext}
          />
        ) : null}
        {stage === STAGES.REVIEW ? (
          <Button
            label={queuing ? "Queuing…" : "Generate PDF"}
            disabled={queuing || orderedIds.length === 0}
            loading={queuing}
            onClick={handleGenerate}
          />
        ) : null}
      </div>
    </div>
  );

  return (
    <div className="mt-6 flex w-full flex-col gap-4">
      <div>
        <div className="text-2xl font-bold">PDF Generator</div>
        <div className="pt-1 text-sm text-gray-500">
          {stageTitle}
          {stage === STAGES.CONFIGURE && multiInstance && currentIndex >= 0
            ? ` — instance ${currentIndex + 1} of ${orderedIds.length}`
            : ""}
        </div>
      </div>

      {listUnresolved && !listLoading ? (
        <div className="text-sm text-red-600">
          That select list holds no instances — add some to it before generating
          a PDF.
        </div>
      ) : null}
      {queryInput == null && !listUnresolved ? (
        <div className="text-sm text-red-600">
          Select instances, artworks or exhibitions (or open a select list, or
          run an instance search) before opening PDF Generator.
        </div>
      ) : null}
      {error ? (
        <div className="text-sm text-red-600">
          Could not load the selected instances: {error.message}
        </div>
      ) : null}
      {(loading || listLoading) && orderedIds.length === 0 ? (
        <div className="text-sm text-gray-500">Loading instances…</div>
      ) : null}
      {!loading && queryInput != null && edges.length === 0 ? (
        <div className="text-sm text-red-600">
          {artworkIds.length > 0
            ? "None of the selected artworks have an instance to generate a PDF from."
            : exhibitionIds.length > 0
              ? "None of the selected exhibitions have any works to generate a PDF from."
              : "No instances matched the selection."}
        </div>
      ) : null}

      {stage === STAGES.ARRANGE && orderedIds.length > 0 ? (
        <>
          <div className="text-sm text-gray-500">
            {reportCoverIncluded
              ? "The PDF opens with its front cover, then each instance in this order"
              : "The PDF opens with the first instance in this order"}{" "}
            — drag to change it. They start in the order of your selection, and
            each instance&apos;s own pages are set in the next step.
            {artworkIds.length > 0
              ? " Each selected artwork contributes its most recent instance."
              : ""}
          </div>
          <div className="flex max-h-[50dvh] flex-col gap-2 overflow-y-auto pr-1 lg:max-h-[55vh]">
            <CoverRow
              cover={reportCover}
              summary={summariesById.get(reportCover.instanceId)}
              included={reportCoverIncluded}
              onEdit={() => setStage(STAGES.COVER)}
            />
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={orderedIds}
                strategy={verticalListSortingStrategy}
              >
                {orderedIds.map((id, index) => (
                  <SortableInstanceRow
                    key={id}
                    summary={
                      summariesById.get(id) || {
                        id,
                        name: `Instance ${id}`,
                        images: [],
                      }
                    }
                    index={index}
                    total={orderedIds.length}
                    onMoveUp={() =>
                      applyOrder(reorderList(orderedIds, index, index - 1))
                    }
                    onMoveDown={() =>
                      applyOrder(reorderList(orderedIds, index, index + 1))
                    }
                    onConfigure={() => goToInstance(id)}
                    onRemove={() => removeInstance(id)}
                  />
                ))}
              </SortableContext>
            </DndContext>
          </div>
        </>
      ) : null}

      {stage === STAGES.COVER && orderedIds.length > 0 ? (
        <>
          <div className="rounded border border-gray-200 bg-gray-50 px-3 py-2 text-sm">
            <label className="flex cursor-pointer items-center gap-2">
              <input
                type="checkbox"
                name="include-front-cover"
                checked={reportCoverIncluded}
                disabled={queuing}
                onChange={event =>
                  updateReportCoverIncluded(event.target.checked)
                }
              />
              <span className="font-medium">Include front cover</span>
            </label>
            <div className="mt-1 text-xs text-gray-500">
              {reportCoverIncluded
                ? "The PDF starts with the cover configured below."
                : "The PDF starts directly with the first instance's pages. Your cover choices are kept in case you turn it back on."}
            </div>
          </div>
          <div className="text-sm text-gray-500">
            {reportCoverIncluded
              ? `One front cover opens the whole PDF — individual instances
                  don't get their own cover pages. ${
                    orderedIds.length === 1
                      ? "The work's own artist, title and year are filled in below; overtype anything you'd rather word differently."
                      : "The title is for the exhibition or series rather than a single work."
                  }`
              : "Turn the front cover back on to edit its wording and image."}
          </div>
          {reportCoverIncluded ? (
            <ReportCoverEditor
              orderedIds={orderedIds}
              summariesById={summariesById}
              cover={reportCover}
              onChange={updateCover}
              disabled={queuing}
            />
          ) : null}
        </>
      ) : null}

      {stage === STAGES.CONFIGURE && currentId != null ? (
        <div className="flex min-w-0 flex-col gap-4 lg:flex-row lg:gap-6">
          {/* Nothing to jump between when the brochure covers one work. */}
          {multiInstance ? (
            <InstanceNavigationList
              orderedIds={orderedIds}
              summariesById={summariesById}
              configurationsById={configurationsById}
              currentId={currentId}
              onSelect={goToInstance}
            />
          ) : null}
          <div className="min-w-0 flex-1">
            <div className="text-lg font-semibold">
              {currentSummary?.name || `Instance ${currentId}`}
            </div>
            <div className="text-sm text-gray-500">
              {[currentSummary?.artistName, currentSummary?.title]
                .filter(Boolean)
                .join(" — ")}
            </div>
            <BrochurePageList
              instanceId={currentId}
              pages={currentPages}
              onChange={pages => updateConfiguration(currentId, { pages })}
              images={currentSummary?.images || []}
              precis={currentSummary?.precis || ""}
              artists={currentSummary?.artists || []}
              publicCollectionsValue={currentConfiguration.publicCollections}
              onPublicCollectionsChange={updatePublicCollections}
              biographiesValue={biographies}
              onBiographyChange={updateBiography}
              artistPortraitsValue={artistPortraits}
              onArtistPortraitChange={updateArtistPortrait}
              portraitCaptionsValue={portraitCaptions}
              onPortraitCaptionChange={updatePortraitCaption}
              disabled={queuing}
            />
          </div>
        </div>
      ) : null}

      {stage === STAGES.REVIEW ? (
        <div className="flex flex-col gap-2">
          <div className="text-sm text-gray-500">
            The PDF{" "}
            {reportCoverIncluded
              ? "opens with its front cover, then prints"
              : "starts by printing"}{" "}
            {multiInstance
              ? "each instance's pages in this order"
              : "the work's pages"}
            . Unpicked image slots fall back to the instance&apos;s primary
            image.
          </div>
          <div className="flex flex-col gap-2 rounded border border-gray-200 bg-gray-50 px-3 py-2 text-sm sm:flex-row sm:items-center sm:justify-between">
            <div className="min-w-0">
              <span className="font-medium">Front cover: </span>
              {reportCoverIncluded ? (
                <>
                  {coverSummaryText(reportCover)}
                  {reportCover.instanceId
                    ? ` (image from ${
                        summariesById.get(reportCover.instanceId)?.name ||
                        `instance ${reportCover.instanceId}`
                      })`
                    : " (no image)"}
                </>
              ) : (
                "Off"
              )}
            </div>
            <button
              type="button"
              className="text-xs font-medium text-blue-600 hover:text-blue-800"
              onClick={() => setStage(STAGES.COVER)}
            >
              Edit
            </button>
          </div>
          <div className="rounded border border-gray-200 bg-gray-50 px-3 py-2 text-sm">
            <label className="flex cursor-pointer items-center gap-2">
              <input
                type="checkbox"
                name="show-sales-price"
                checked={pricesIncluded}
                disabled={queuing}
                onChange={event => updatePricesIncluded(event.target.checked)}
              />
              <span className="font-medium">Show sales price</span>
            </label>
            <div className="mt-1 text-xs text-gray-500">
              {pricesIncluded
                ? "Each work's price prints under its catalogue details, with the applicable taxes note."
                : "No prices anywhere in the PDF."}
              {cohortSource.artworkIds.length > 0
                ? " These works were picked in Artworks, so the price is the one held by the instance this PDF prints — the most recent of each artwork, whose images and catalogue details it also prints."
                : ""}
            </div>
          </div>
          <div className="max-h-[50dvh] overflow-auto lg:max-h-[50vh]">
            <table className="w-full min-w-[42rem] border-collapse text-left text-sm">
              <thead>
                <tr className="border-b border-gray-200 text-xs uppercase tracking-wide text-gray-500">
                  <th className="px-3 py-2">#</th>
                  <th className="px-3 py-2">Instance</th>
                  <th className="px-3 py-2">Pages</th>
                  <th className="px-3 py-2">Images</th>
                  <th className="px-3 py-2" />
                </tr>
              </thead>
              <tbody>
                {orderedIds.map((id, index) => {
                  const summary = summariesById.get(id);
                  const pages = sectionPagesOf(configurationsById[id], {
                    multiInstance,
                  });
                  return (
                    <tr key={id} className="border-b border-gray-100">
                      <td className="px-3 py-2 text-gray-400">{index + 1}</td>
                      <td className="px-3 py-2">
                        <div className="font-medium">
                          {summary?.name || `Instance ${id}`}
                        </div>
                        <div className="text-xs text-gray-500">
                          {[summary?.artistName, summary?.title]
                            .filter(Boolean)
                            .join(" — ")}
                        </div>
                      </td>
                      <td className="px-3 py-2">{sectionSummary(pages)}</td>
                      <td className="px-3 py-2">
                        {sectionImagesSummary(pages)}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <div className="flex justify-end gap-3">
                          <button
                            type="button"
                            className="text-xs font-medium text-blue-600 hover:text-blue-800"
                            onClick={() => goToInstance(id)}
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            disabled={orderedIds.length === 1}
                            className="text-xs font-medium text-red-600 enabled:hover:text-red-800 disabled:cursor-not-allowed disabled:opacity-30"
                            title={
                              orderedIds.length === 1
                                ? "A report must contain at least one instance"
                                : "Remove from report"
                            }
                            onClick={() => removeInstance(id)}
                          >
                            Remove
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}

      {footer}
    </div>
  );
};

export { BrochureWizard };
export default BrochureWizard;
